import { Emitter } from "@core/events/emitter";
import type { EditorUIVisibility, IEditorUIModel } from "./IEditorUIModel";

/**
 * `EditorUIModel` — port of the `IEditorUIModel` half of Unity's
 * `EditorViewModel` (`CampfireScenes/Assets/Campfire/Runtime/ViewModels/EditorViewModel.cs:47-94, :500-516`).
 *
 * Headless and dependency-free: it is nothing but the two booleans, their
 * derived `isAllUIHidden`, and a change emitter. Everything about HOW the
 * panels leave the screen lives in `ProjectView.vue`; see `IEditorUIModel` for
 * why this state is an IOC model rather than a composable.
 */
export class EditorUIModel implements IEditorUIModel {
  readonly onChanged = new Emitter<EditorUIVisibility>();

  // Unity `m_isScensListVisible` / `m_isHierarchyVisible` — both default TRUE,
  // i.e. the editor opens fully expanded. (The Unity field name's missing "e"
  // is verbatim upstream; not reproduced here.)
  private m_isScenesListVisible = true;
  private m_isHierarchyVisible = true;

  get isScenesListVisible(): boolean {
    return this.m_isScenesListVisible;
  }

  get isHierarchyVisible(): boolean {
    return this.m_isHierarchyVisible;
  }

  /** Unity `IsAllUIHidden` => `!IsScenesListVisible && !IsHierarchyVisible`. */
  get isAllUIHidden(): boolean {
    return !this.m_isScenesListVisible && !this.m_isHierarchyVisible;
  }

  setScenesListVisible(visible: boolean): void {
    if (this.m_isScenesListVisible === visible) return;
    this.m_isScenesListVisible = visible;
    this.emitChanged();
  }

  setHierarchyVisible(visible: boolean): void {
    if (this.m_isHierarchyVisible === visible) return;
    this.m_isHierarchyVisible = visible;
    this.emitChanged();
  }

  toggleScenesList(): void {
    this.setScenesListVisible(!this.m_isScenesListVisible);
  }

  toggleHierarchy(): void {
    this.setHierarchyVisible(!this.m_isHierarchyVisible);
  }

  /**
   * Unity `ShowAllExpandableUI()` / `HideAllExpandableUI()` — set BOTH flags in
   * one shot and emit once, so a subscriber never observes the intermediate
   * half-applied state. Unity assigns them on one line
   * (`IsScenesListVisible = IsHierarchyVisible = true`) for the same reason.
   */
  showAllExpandableUI(): void {
    this.setAll(true);
  }

  hideAllExpandableUI(): void {
    this.setAll(false);
  }

  private setAll(visible: boolean): void {
    if (this.m_isScenesListVisible === visible && this.m_isHierarchyVisible === visible) return;
    this.m_isScenesListVisible = visible;
    this.m_isHierarchyVisible = visible;
    this.emitChanged();
  }

  private emitChanged(): void {
    this.onChanged.emit({
      isScenesListVisible: this.m_isScenesListVisible,
      isHierarchyVisible: this.m_isHierarchyVisible,
    });
  }
}
